import React, { Component } from 'react';
import classNames from 'classnames';

import { DraggableData } from 'react-draggable';
import { HashMap, Option, HashSet } from 'prelude-ts';

import ResourseBox from './ResourceBox';
import RequirementBox from './RequirementBox';

import Formula from '../formula/Formula';
import Statement from '../Statement';
import { PositionedNode, Point } from '../Position';

import styles from '../styles/Subgoal.module.css';
import arrow from '../images/arrow.svg';
import {
  Rule,
  PreTensorRight,
  TensorRight,
  PreParLeft,
  ParLeft,
  Highlight
} from '../Rule';

/**
 * Stores position information for formula boxes.
 */
export class FormulaPositions {
  private readonly positions: HashMap<Formula['id'], PositionedNode>;

  private constructor(positions: HashMap<Formula['id'], PositionedNode>) {
    this.positions = positions;
  }

  /**
   * The empty position map storing no locations.
   */
  static empty(): FormulaPositions {
    return new FormulaPositions(HashMap.empty());
  }

  /**
   * Add a new node to the position map.
   *
   * If the new node is already in the map, the entry is updated
   * instead of being replaced, preserving drag offset information.
   */
  add(formula: Formula['id'], node: HTMLElement): FormulaPositions {
    const oldEntry = this.positions.get(formula);
    const oldOffset = oldEntry.isSome() ? oldEntry.get().offset : Point.origin;
    const newEntry = PositionedNode.of(node, oldOffset);
    return new FormulaPositions(this.positions.put(formula, newEntry));
  }

  /**
   * Update the offset information of an existing formula.
   */
  update(formula: Formula['id'], newOffset: Point): FormulaPositions {
    const oldEntry = this.positions.get(formula).getOrThrow();
    const newEntry = oldEntry.setOffset(newOffset);
    return new FormulaPositions(this.positions.put(formula, newEntry));
  }

  /**
   * Get the position information of an existing formula.
   */
  get(formula: Formula['id']): Option<PositionedNode> {
    return this.positions.get(formula);
  }
}

export interface FormulaPositionMonitor {
  /* Gets called when a formula box is constructed. */
  onCreate(formula: Formula['id'], node: HTMLElement): void;

  /* Gets called when a formula box is dragged. */
  onDrag(formula: Formula['id'], newOffset: DraggableData): void;
}

interface Props {
  statement: Statement;
  executor: (statement: Statement['id'], rule: Rule) => void;
  draggingEnabled: boolean;
  tooltip: boolean;
}

interface State {
  /** Positions of all the formula boxes. */
  positions: FormulaPositions;
  splitPoint: Formula['id'] | undefined;
}

class Subgoal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { positions: FormulaPositions.empty(), splitPoint: undefined };
  }

  /**
   * Split resources and requirements into those that lie above the center
   * center point of the given formula and those that lie below.
   * @param at
   */
  private splitWorld(
    at: Formula['id']
  ): { above: HashSet<Formula['id']>; below: HashSet<Formula['id']> } {
    const formulae: HashSet<
      Formula['id']
    > = this.props.statement.resources
      .appendAll(this.props.statement.requirements)
      .toSet(f => f.id);

    const center = this.state.positions.get(at).getOrThrow().center.y;
    const above = formulae.filter(
      id => this.state.positions.get(id).getOrThrow().center.y <= center
    );
    const below = formulae.filter(
      id => this.state.positions.get(id).getOrThrow().center.y > center
    );

    return { above: above, below: below };
  }

  private executor = (rule: Rule) => {
    if (rule instanceof PreTensorRight) {
      const split = this.splitWorld(rule.tensor.id);
      rule = new TensorRight(rule.tensor, split.above, split.below);
    } else if (rule instanceof PreParLeft) {
      const split = this.splitWorld(rule.par.id);
      rule = new ParLeft(rule.par, split.above, split.below);
    } else if (rule instanceof Highlight) {
      this.onSplitHighlight(rule.point);
      return;
    }

    this.props.executor(this.props.statement.id, rule);
  };

  /**
   * Called when a formula box is constructed.
   */
  private onFormulaCreate = (formula: Formula['id'], node: HTMLElement) => {
    this.setState(state => ({
      positions: state.positions.add(formula, node)
    }));
  };

  /**
   * Called when a formula box is dragged.
   */
  private onFormulaDrag = (formula: Formula['id'], offset: DraggableData) => {
    this.setState(state => ({
      positions: state.positions.update(formula, new Point(offset.x, offset.y))
    }));
  };

  private onSplitHighlight = (formula: Formula['id'] | undefined) => {
    this.setState({
      splitPoint: formula
    });
  };

  private formulaPositionMonitor: FormulaPositionMonitor = {
    onCreate: this.onFormulaCreate,
    onDrag: this.onFormulaDrag
  };

  render() {
    let splits, dividerTop;
    if (this.state.splitPoint !== undefined) {
      splits = this.splitWorld(this.state.splitPoint);
      splits = {
        above: splits.above.remove(this.state.splitPoint),
        below: splits.below.remove(this.state.splitPoint)
      };
      dividerTop = this.state.positions.get(this.state.splitPoint).getOrThrow()
        .center.y;
    } else {
      splits = {
        above: HashSet.empty<Formula['id']>(),
        below: HashSet.empty<Formula['id']>()
      };
    }
    return (
      <div className={styles.subgoal} id="subgoal">
        <ResourseBox
          subgoal={this.props.statement}
          formulae={this.props.statement.resources}
          executor={this.executor}
          draggingEnabled={this.props.draggingEnabled}
          tooltip={this.props.tooltip}
          formulaPositionMonitor={this.formulaPositionMonitor}
          positions={this.state.positions}
          splits={splits}
        />
        <img className={styles.arrow} src={arrow} alt="-->" draggable={false} />
        <div
          className={classNames(styles.divider, {
            [styles.disabled]: dividerTop === undefined
          })}
          style={{ top: dividerTop }}
        />
        <RequirementBox
          subgoal={this.props.statement}
          formulae={this.props.statement.requirements}
          executor={this.executor}
          draggingEnabled={this.props.draggingEnabled}
          tooltip={this.props.tooltip}
          formulaPositionMonitor={this.formulaPositionMonitor}
          positions={this.state.positions}
          splits={splits}
        />
      </div>
    );
  }
}

export default Subgoal;
